/**
 * 강화 수집(확장검색 레벨2) 플랜 — `subscriptionPlan.planService.enhancedSearchLV2`.
 *
 * 이 필드는 기본 PlanService 표에 없고 워크스페이스 문서에만 직접 들어간다.
 * 값은 "키워드 × 채널" 토글 상한(숫자)이며, 필드 자체의 유무가 곧 기능 보유 여부다.
 *
 * 소비처
 * - 타겟 키워드 설정의 키워드별 TikTok·Instagram 강화 수집 섹션 (상한 표시·초과 차단)
 * - 키워드 모니터링 사이드바의 "확장 검색" 토글 노출 (기본 검색 코퍼스 isBasicSearch 해제)
 */

/**
 * 워크스페이스 문서에서 강화 수집 상한을 읽는다.
 * 필드가 없거나 값이 유효하지 않으면 null — 이 경우 관련 기능을 노출하지 않는다.
 * @param {Object|null|undefined} workspaceDoc
 * @returns {number|null}
 */
export function resolveEnhancedCollectionLimit(workspaceDoc: any) {
  const raw = workspaceDoc?.subscriptionPlan?.planService?.enhancedSearchLV2;
  if (raw === undefined || raw === null || raw === '') return null;
  const num = Number(raw);
  if (!Number.isFinite(num) || num < 0) return null;
  return Math.floor(num);
}

/**
 * 강화 수집(확장검색 레벨2) 플랜 보유 워크스페이스인지.
 * @param {Object|null|undefined} workspaceDoc
 * @returns {boolean}
 */
export function hasEnhancedSearchLv2Plan(workspaceDoc: any): boolean {
  return resolveEnhancedCollectionLimit(workspaceDoc) !== null;
}

/** 강화 수집 채널 id → 표시 라벨 (설정 폼의 칩과 동일) */
const ENHANCED_COLLECTION_PLATFORM_LABELS: Record<string, string> = {
  tiktok: 'TikTok',
  instagram: 'Instagram',
};

/**
 * 캠페인의 `enhancedCollections`([{ keyword, platforms }])를 키워드 → 채널 목록 Map으로.
 * 키는 소문자 트림 — 칩 라벨과 저장값의 대소문자가 달라도 매칭된다.
 * @param {Object|null|undefined} campaign
 * @returns {Map<string, string[]>}
 */
export function buildEnhancedCollectionKeywordMap(campaign: any) {
  const list = Array.isArray(campaign?.enhancedCollections)
    ? campaign.enhancedCollections
    : [];
  const map = new Map();
  for (const entry of list) {
    const keyword = String(entry?.keyword ?? '').trim().toLowerCase();
    if (!keyword) continue;
    const platforms = (Array.isArray(entry?.platforms) ? entry.platforms : [])
      .map((v: unknown) => String(v ?? '').trim().toLowerCase())
      .filter(Boolean);
    if (platforms.length === 0) continue;
    map.set(keyword, platforms);
  }
  return map;
}

/**
 * 강화 수집 채널 표시 문자열 (예: "TikTok · Instagram"). 비어 있으면 ''.
 * @param {string[]} platforms
 * @returns {string}
 */
export function formatEnhancedCollectionPlatforms(platforms: string[]): string {
  return (Array.isArray(platforms) ? platforms : [])
    .map((id: string) => ENHANCED_COLLECTION_PLATFORM_LABELS[id] ?? id)
    .filter(Boolean)
    .join(' · ');
}
